// #326 · cómo se rotula una cifra que sale de una consulta con tope.
//
// Un conteo o un importe que viene de una consulta capada NO es el total, y la pantalla tiene que
// decirlo: quien mira la cifra toma decisiones con ella. La #325 fijó la forma de decirlo en el panel
// «No planificables» del calendario y esto es ese mismo patrón, extraído para que las dos tablas del
// Dashboard de Control (la de líneas y la de «Anomalías a revisar») no lo escriban cada una a su
// manera. Los literales son los de la #325 a propósito: dos sitios que cuentan lo mismo no pueden
// decirlo de dos formas distintas.
//
// LA REGLA, que es la que importa: aquí NO se compara nada. El total y lo que se enseña vienen del
// servidor, de la misma consulta y del mismo instante, con la conclusión ya hecha en `completitud`.
// Hasta la decisión de Dirección del 08/10/2026 el total se pedía aparte y la pantalla comparaba las
// dos cifras para decidir si había recorte; las dos lecturas no coincidían en seis cosas —el dedup
// del espejo, la copia elegida, el importe, el minuto de retraso del caché, el orden y el tope— y
// cuatro rondas de revisión seguidas arreglaron una cada una y descubrieron la siguiente. Si en este
// fichero aparece un `>` entre una cifra de la lista y una del total, la familia está reabierta.
//
// DÓNDE NO SE USA, y no es un olvido: `src/pages/calendario/calendario.vue` conserva su propia copia
// de esta lógica. Su banco de pruebas (`scripts/prueba-panel-no-planificables-total.js`) carga el
// `<script>` del componente QUITÁNDOLE los `import` y resolviendo lo desconocido con dobles, así que
// un `import` de este módulo dejaría las comprobaciones de sus rótulos evaluando un doble en vez del
// texto. Unificarlo pide reescribir ese banco, que son 758 líneas y no es de esta tarea: queda
// anotado en el inventario de la #326. Mientras tanto, los literales están aquí Y allí, y si uno
// cambia hay que cambiar los dos. La excepción son `totalAparte` y la frase de «ninguna está entre
// las cargadas», que nacen de la sección de anomalías y allí no se recorren: copiarlas sería código
// muerto. Lo que allí SÍ se cumple es la regla de este módulo: su `resumenNegocio` solo calla con
// cero filas, y allí el cero demuestra el vacío.
//
// #543 · se ha retirado la opción `subconjunto`. La puso la #326 para la tabla de anomalías, que
// filtraba por chip EN EL CLIENTE sobre la página cargada: allí cero filas podía ser del filtro o
// del tope y no probaba nada. Desde que el chip filtra en el servidor no queda ningún sitio que
// pinte un subconjunto de la página, así que la excepción se fue con su motivo en vez de quedarse
// como una rama que nadie recorre y que la próxima pantalla copiaría sin saber para qué era.

// Los tres valores que el servidor puede afirmar (`ResumenRecorteDTO` en la API, que a su vez los
// toma de `BloqueadosNegocioResumenDTO` de la #325). Cualquier otra cosa —un valor nuevo que esta
// pantalla no conozca— se lee como «no lo sé» y NUNCA como «completo»: afirmar que no falta nada es
// justo lo que no se puede hacer sin haberlo medido.
const COMPLETO = 'COMPLETO';
const PARCIAL = 'PARCIAL';
const DESCONOCIDO = 'DESCONOCIDO';

// Por qué no se tiene el total. Las tres se dicen distintas porque la acción que toca es distinta: la
// primera es un despliegue desparejado, la segunda es un fallo del servidor al derivarla y la tercera
// es un contrato roto. Quien prueba no tiene el registro del servidor delante: lo tiene que decir la
// pantalla (§9 del CLAUDE.md del orquestador).
const MOTIVOS_SIN_TOTAL = {
    ausente: 'este servidor no envía el total junto a la lista',
    desconocido: 'el servidor no ha podido calcularlo',
    cuerpo: 'el servidor ha contestado algo que no se entiende'
};

/**
 * ¿Es esta cifra un cero medido? Un campo que no viene, un `null` o un `undefined` NO son cero:
 * leerlos como cero convierte «no lo sé» en «no hay nada», que es exactamente el defecto que esta
 * tarea viene a quitar. Por eso el 0 tiene que ser un número de verdad y no un valor falsy.
 */
function esCero(valor) {
    return typeof valor === 'number' && valor === 0;
}

/**
 * Lo que la pantalla PUEDE AFIRMAR de la tabla que está pintando.
 *
 * Las cifras de «lo que se enseña» salen de la LISTA PINTADA (`mostradas`, `importeMostrado`) y no
 * del resumen, aunque sean el mismo número: lo que la cabecera promete tiene que cuadrar con las
 * filas que hay debajo. Si un día llegaran distintos, lo honesto es decir lo que se está pintando.
 *
 * Cuatro situaciones, y cada una se dice distinta en pantalla (`modo`):
 *   - 'vacio':     no hay filas Y está probado que no hay nada detrás, así que no hay tope del que
 *                  hablar. Es el ÚNICO modo que calla sin rotular nada.
 *   - 'completo':  la página ES todo el conjunto. Se rotulan sus cifras, sin coletilla: no se avisa
 *                  de un recorte que no existe.
 *   - 'recorta':   el tope ha recortado. Se rotula el total y qué parte se está viendo.
 *   - 'sin-total': no se sabe el total, y la nota dice POR QUÉ. Nunca se rotula la cifra de la
 *                  página como si fuera el total.
 *
 * EL SILENCIO NECESITA UNA PRUEBA, y esta es la regla que cierra la familia que trajeron dos rondas
 * de revisión seguidas (la 1 y la 3 de la #326): callarse —modo 'vacio'— es afirmar «esto es todo lo
 * que hay», y eso no se puede deducir de que FALTEN datos. Lo prueban tres cosas, y solo tres:
 *   1. que el servidor lo diga (`completitud` COMPLETO del conjunto que se está pintando),
 *   2. el cero de las FILAS: lo que se pinta es lo que el servidor ha mandado del conjunto que se
 *      está pintando, y ningún tope convierte un conjunto con filas en cero filas, o
 *   3. que un total SIN TOPE del conjunto que se pinta valga cero: un conjunto sin filas no tiene
 *      nada que esconder detrás del tope, y da igual de dónde venga la página.
 * El cero del chip (3) importa porque no lleva tope: no tenerlo en cuenta era avisar de que no se
 * puede comprobar la lista justo al lado de un chip diciendo 0, que es el §9 del CLAUDE.md del
 * orquestador. Por eso quien llama DECLARA con qué cifra sin tope cuenta (`totalConjunto`) en vez de
 * que este módulo lo adivine, y la decisión de callar o no vive aquí y solo aquí: los rótulos, la
 * nota y el pie de la tabla la leen de este objeto (ver {@link estaVacia}).
 *
 * De esas cifras sin tope se lee ÚNICAMENTE el cero. No se comparan con `mostradas` para deducir un
 * recorte: eso es volver a enfrentar dos lecturas en la pantalla, que es la familia que cerró la #325
 * y que la cabecera de este fichero prohíbe reabrir. Un cero no es una comparación —es la ausencia
 * del conjunto entero— y por eso sí se puede leer.
 *
 * @param {object} resumen        el `resumen`/`lineasResumen` tal como lo manda la API
 * @param {number} mostradas      filas que se están pintando
 * @param {number} importeMostrado Σ importe de esas filas
 * @param {object} [opciones]     `{ totalConjunto: n }` con el nº de filas del conjunto que se pinta
 *        según una fuente SIN tope —el contador del chip, que sale del mismo `$facet`—, cuando quien
 *        llama tiene una. Solo se lee su cero; si no se sabe, se omite o se pasa `null`, nunca un 0
 *        de consolación.
 */
function estado(resumen, mostradas, importeMostrado, opciones) {
    const n = mostradas || 0;
    const imp = importeMostrado || 0;
    const base = { mostradas: n, importeMostrado: imp, lineas: n, importe: imp };
    // Las tres únicas pruebas del vacío (ver la regla del javadoc). `declara` es «el servidor ha dicho
    // algo de la completitud»: su ausencia nunca es una prueba de nada por sí sola.
    const declara = !!(resumen && resumen.completitud);
    // Un total sin tope que vale cero. Vale el que declara quien llama y el del propio resumen, que
    // también se deriva antes del tope: son la misma prueba por dos vías, y la regla no se reparte
    // según cuál de las dos haya llegado.
    const conjuntoEnCero = esCero(opciones && opciones.totalConjunto)
        || (!!resumen && esCero(resumen.total));
    if (!n && (!declara || resumen.completitud === COMPLETO || conjuntoEnCero)) {
        return Object.assign(base, { modo: 'vacio', motivo: null });
    }
    // Un despliegue desparejado (front nuevo, API anterior) no manda el resumen. No se adivina.
    if (!resumen || !resumen.completitud) {
        return Object.assign(base, { modo: 'sin-total', motivo: 'ausente' });
    }
    if (resumen.completitud === DESCONOCIDO) {
        return Object.assign(base, { modo: 'sin-total', motivo: 'desconocido' });
    }
    if (resumen.completitud === COMPLETO) {
        return Object.assign(base, { modo: 'completo', motivo: null });
    }
    if (resumen.completitud !== PARCIAL) {
        return Object.assign(base, { modo: 'sin-total', motivo: 'cuerpo' });
    }
    const total = typeof resumen.total === 'number' ? resumen.total : NaN;
    const importeTotal = typeof resumen.importeTotal === 'number' ? resumen.importeTotal : NaN;
    if (!isFinite(total) || total < 0 || !isFinite(importeTotal)) {
        return Object.assign(base, { modo: 'sin-total', motivo: 'cuerpo' });
    }
    return Object.assign(base, { modo: 'recorta', motivo: null, lineas: total, importe: importeTotal });
}

/**
 * Las cifras de la cabecera. Cuando no se sabe el total, son las de lo que se enseña y lo dice ahí
 * mismo en vez de dejarlo entender.
 *
 * @param {object} e      lo que devuelve {@link estado}
 * @param {object} fmt    { num, eur } — el formateo lo pone la pantalla, no este módulo
 * @param {string} unidad lo que se cuenta («líneas»)
 */
function cifras(e, fmt, unidad) {
    if (e.modo === 'sin-total') {
        return fmt.num(e.mostradas) + ' ' + unidad + ' mostradas · '
            + fmt.eur(e.importeMostrado) + ' en lo que se enseña';
    }
    return fmt.num(e.lineas) + ' ' + unidad + ' · ' + fmt.eur(e.importe);
}

/**
 * La nota que impide leer las cifras como si fueran todo. Solo aparece cuando hay algo que advertir:
 * si la página es el conjunto entero no se avisa de un truncamiento que no existe, y sin filas no hay
 * tope del que hablar. Y si no se sabe el total, dice por qué no se sabe: un control que no puede dar
 * lo que promete no se queda mudo (§9 del CLAUDE.md del orquestador).
 *
 * `modo: 'sin-total'` NO es silencio. Es el caso en el que la tabla está más desamparada —hay filas,
 * nadie sabe si son todas— y callar ahí devuelve a la pantalla a decir una cifra de página sin
 * explicación, que es justo el defecto de esta tarea.
 *
 * @param {object} e  lo que devuelve {@link estado}
 * @param {object} [opciones] `{ totalAparte: true }` cuando el total del conjunto lo publica OTRO
 *        control de la misma sección —los chips y las tarjetas de «Anomalías a revisar», que salen de
 *        la misma respuesta y no llevan tope—. Entonces la frase habla de si la LISTA está completa y
 *        no de un total que falta: con un chip diciendo 1.240 al lado, «no se ha podido obtener el
 *        total» serían dos partes de la pantalla contradiciéndose (§9). La causa es la misma y el
 *        vocabulario sigue siendo uno: cambia de qué se está hablando, no cómo se dice.
 *        `{ conTotal: true, num: fn }` cuando la frase tiene que llevar DENTRO las dos cifras —«se
 *        muestran 259 de 4.485»—, porque quien llama no tiene una línea de cifras encima que ya
 *        publique el total. Es lo que pidió Dirección para «Anomalías a revisar» (#543): allí la
 *        tabla no tiene cabecera de cifras y el total solo estaba en el chip, dos controles más
 *        arriba. La cabecera de la tabla de líneas NO lo pasa: su `cifras` dice el total justo
 *        encima, y repetirlo sería decir lo mismo dos veces en el mismo renglón. `num` es el
 *        formateo de miles de la pantalla, igual que en {@link cifras}: este módulo no formatea.
 */
function nota(e, opciones) {
    if (e.modo === 'vacio' || e.modo === 'completo') {
        return null;
    }
    if (e.modo === 'recorta') {
        // Con un filtro encima puede no entrar NINGUNA en lo cargado (todas las de ese estado son de
        // importe bajo). «se muestran las 0 de mayor importe» no es español: lo que hay que decir es
        // que la tabla está vacía por el tope y no porque no haya nada.
        if (!e.mostradas) {
            return 'ninguna está entre las cargadas, que son las de mayor importe';
        }
        const num = (opciones && opciones.num) || function (v) { return String(v); };
        return (opciones && opciones.conTotal)
            ? 'se muestran ' + num(e.mostradas) + ' de ' + num(e.lineas) + ', las de mayor importe'
            : 'se muestran las ' + num(e.mostradas) + ' de mayor importe';
    }
    // La causa solo se nombra si se conoce: afirmar «el servidor ha fallado» cuando no se sabe sería
    // cambiar una cifra inventada por una culpa inventada.
    const causa = MOTIVOS_SIN_TOTAL[e.motivo];
    const cabeza = (opciones && opciones.totalAparte)
        ? 'no se ha podido comprobar si la lista está completa'
        : 'no se ha podido obtener el total';
    return cabeza + (causa ? ' · ' + causa : '');
}

/**
 * ¿Puede la pantalla decir que la tabla está vacía PORQUE NO HAY NADA?
 *
 * Existe para que el pie de la tabla —«Sin anomalías para el filtro actual.»— no lo decida por su
 * cuenta con un `filas.length === 0`. Afirmar que no hay nada es la misma afirmación que hace el
 * silencio de {@link nota}, así que la saca el mismo sitio: si no, las dos partes de la tabla acaban
 * diciendo lo contrario —el pie «no hay anomalías» y la nota «ninguna está entre las cargadas»—, que
 * es el defecto del §9 del CLAUDE.md del orquestador contado dos veces.
 *
 * Cuando esto es `false` con cero filas, el pie dice lo que se enseña («0 líneas mostradas») y la
 * nota de arriba explica por qué puede no ser todo. Las dos frases son ciertas y no se contradicen.
 *
 * @param {object} e lo que devuelve {@link estado}
 */
function estaVacia(e) {
    return !!e && e.modo === 'vacio';
}

export default {
    COMPLETO: COMPLETO,
    PARCIAL: PARCIAL,
    DESCONOCIDO: DESCONOCIDO,
    MOTIVOS_SIN_TOTAL: MOTIVOS_SIN_TOTAL,
    estado: estado,
    cifras: cifras,
    nota: nota,
    estaVacia: estaVacia
};
